import Phaser from 'phaser';
import { Layout, Palette, Typography } from '@/ui/theme';

export interface ButtonOptions {
  width?: number;
  height?: number;
  fillColor?: number;
  hoverColor?: number;
  textColor?: string;
  fontSize?: number;
  cornerRadius?: number;
  /** Callback disparado em pointerup (clique/toque concluído). */
  onClick?: () => void;
}

/**
 * Botão simples reutilizável: retângulo arredondado + texto centralizado.
 *
 * Estende Container para que `x`/`y` posicionem o botão como um todo, e o
 * fundo + texto fiquem agrupados. Estados visuais (hover, pressed) são
 * aplicados via tint na Graphics — sem tweens caros para manter performance
 * em mobile (CLAUDE.md §13).
 *
 * Tamanho mínimo respeita 48x48 (toque confortável — §8).
 */
export class Button extends Phaser.GameObjects.Container {
  private readonly background: Phaser.GameObjects.Graphics;
  private readonly label: Phaser.GameObjects.Text;
  private readonly fillColor: number;
  private readonly hoverColor: number;
  private readonly buttonWidth: number;
  private readonly buttonHeight: number;
  private readonly cornerRadius: number;

  constructor(scene: Phaser.Scene, x: number, y: number, text: string, options: ButtonOptions = {}) {
    super(scene, x, y);

    this.buttonWidth = Math.max(options.width ?? 240, Layout.minTouchTarget);
    this.buttonHeight = Math.max(options.height ?? 64, Layout.minTouchTarget);
    this.fillColor = options.fillColor ?? Palette.primary;
    this.hoverColor = options.hoverColor ?? Palette.accent;
    this.cornerRadius = options.cornerRadius ?? Layout.cornerRadius;

    this.background = scene.add.graphics();
    this.drawBackground(this.fillColor);

    this.label = scene.add
      .text(0, 0, text, {
        fontFamily: Typography.fontFamily,
        fontSize: `${options.fontSize ?? 28}px`,
        color: options.textColor ?? Palette.textPrimaryCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add([this.background, this.label]);

    this.setSize(this.buttonWidth, this.buttonHeight);
    this.setInteractive({ useHandCursor: true });

    this.on(Phaser.Input.Events.POINTER_OVER, () => this.drawBackground(this.hoverColor));
    this.on(Phaser.Input.Events.POINTER_OUT, () => {
      this.drawBackground(this.fillColor);
      this.setScale(1);
    });
    this.on(Phaser.Input.Events.POINTER_DOWN, () => this.setScale(0.97));
    this.on(Phaser.Input.Events.POINTER_UP, () => {
      this.setScale(1);
      options.onClick?.();
    });

    scene.add.existing(this);
  }

  setLabel(text: string): this {
    this.label.setText(text);
    return this;
  }

  /**
   * Pulso verde + scale-up curto para confirmar acerto. Após o pulso,
   * `disable()` é chamado para evitar cliques duplos antes da próxima
   * conta entrar.
   */
  flashCorrect(): void {
    this.disableInteractive();
    this.drawBackground(Palette.success);
    this.scene.tweens.add({
      targets: this,
      scale: { from: 1, to: 1.08 },
      duration: 140,
      yoyo: true,
      ease: 'Cubic.easeOut',
    });
  }

  /**
   * Tremida horizontal + cor de aviso para sinalizar resposta errada.
   * Volta sozinho à posição original via tween yoyo.
   */
  flashWrong(): void {
    this.disableInteractive();
    this.drawBackground(Palette.danger);
    const originalX = this.x;
    this.scene.tweens.add({
      targets: this,
      x: { from: originalX - 8, to: originalX + 8 },
      duration: 70,
      yoyo: true,
      repeat: 2,
      onComplete: () => this.setX(originalX),
    });
  }

  /** Destaca como "esta era a resposta certa" — mais sutil que flashCorrect. */
  highlightAsCorrect(): void {
    this.drawBackground(Palette.success);
  }

  private drawBackground(color: number): void {
    this.background.clear();
    this.background.fillStyle(color, 1);
    this.background.fillRoundedRect(
      -this.buttonWidth / 2,
      -this.buttonHeight / 2,
      this.buttonWidth,
      this.buttonHeight,
      this.cornerRadius,
    );
  }
}
